import { GAP_CLIP_MAX_SECONDS, GAP_CLIP_SAMPLE_RATE } from "@rosc/contracts";
import { pcm16ToWav } from "./wav";

/** A line of a gap, in seconds from the start of the gap. */
export type GapClipSegment = {
  start: number;
  end: number;
  speaker: number | null;
  text: string;
};

/** Linear resampling of mono PCM16 (24 kHz capture -> 16 kHz clip). */
export const resamplePcm16 = (
  pcm: Int16Array,
  from: number,
  to: number,
): Int16Array => {
  if (from === to) return pcm;
  const length = Math.floor((pcm.length * to) / from);
  const out = new Int16Array(length);
  const step = from / to;
  for (let i = 0; i < length; i++) {
    const position = i * step;
    const index = Math.floor(position);
    const next = Math.min(index + 1, pcm.length - 1);
    const fraction = position - index;
    out[i] = Math.round(
      (pcm[index] as number) * (1 - fraction) +
        (pcm[next] as number) * fraction,
    );
  }
  return out;
};

const toBase64 = async (blob: Blob): Promise<string> => {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  let binary = "";
  const step = 0x8000;
  for (let start = 0; start < bytes.length; start += step)
    binary += String.fromCharCode(...bytes.subarray(start, start + step));
  return btoa(binary);
};

/**
 * Transcribes a gap of the live transcript with the API's batch
 * speech-to-text (the path the full recording uses after Stop). The audio
 * is the copy kept on this device; the API keeps nothing.
 */
export const transcribeGapClip = async ({
  audio,
  sampleRate,
  inputLanguage,
  diarize,
  signal,
}: {
  audio: Int16Array;
  sampleRate: number;
  inputLanguage: string;
  diarize: boolean;
  signal: AbortSignal;
}): Promise<GapClipSegment[]> => {
  const clip = resamplePcm16(audio, sampleRate, GAP_CLIP_SAMPLE_RATE).subarray(
    0,
    GAP_CLIP_MAX_SECONDS * GAP_CLIP_SAMPLE_RATE,
  );
  const body = {
    audio: await toBase64(pcm16ToWav(clip, GAP_CLIP_SAMPLE_RATE)),
    language: inputLanguage || "auto",
    diarize,
  };
  // Loaded when a gap needs it: the recorder hook stays free of the API
  // client and account modules.
  const [{ referenceClient }, { getVisitScope }] = await Promise.all([
    import("@/lib/orpc/client"),
    import("@/lib/visit-queries"),
  ]);
  signal.throwIfAborted();
  const result = await referenceClient.createGapTranscription(
    { query: {}, body },
    { context: { expectedUserUid: getVisitScope()?.uid }, signal },
  );
  return result.segments.map((segment) => ({
    start: segment.startMs / 1000,
    end: segment.endMs / 1000,
    speaker: segment.speaker,
    text: segment.text,
  }));
};
